import React, { useEffect, useState } from 'react';
import portalApi from '../../../../lib/portalApi';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';

export default function StartupCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Section 2: Show (Filter pills)
  const [activeFilters, setActiveFilters] = useState({
    meetings: true,
    deadlines: true,
    tasks: true,
  });

  // Section 4: Selected meeting for details
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      setError('');

      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const start = new Date(year, month - 1, 1).toISOString();
      const end = new Date(year, month + 2, 0).toISOString();

      const calRes = await portalApi.getStartupPortalCalendar({
        start,
        end,
      });

      const fetchedItems = calRes?.items || [];
      setItems(fetchedItems);
    } catch (err) {
      console.error('Failed to load startup calendar data:', err);
      setError(err.message || 'Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalendarData();
  }, [currentDate]);

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  // Helper date conversions
  const getItemDateStr = (item) => {
    if (!item?.date) return '';
    if (typeof item.date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(item.date)) {
      if (item.type === 'Deadline' || item.type === 'Task') {
        return item.date.slice(0, 10);
      }
    }
    const d = new Date(item.date);
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
  };

  const getCellDateStr = (cellDate) => {
    if (!cellDate) return '';
    const yr = cellDate.getFullYear();
    const mo = String(cellDate.getMonth() + 1).padStart(2, '0');
    const da = String(cellDate.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
  };

  const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return getCellDateStr(d1) === getCellDateStr(d2);
  };

  const isPastTime = (itemDate) => {
    return new Date(itemDate) < new Date();
  };

  // Filtered items based on toggle pills
  const filteredItems = items.filter((item) => {
    if (item.type === 'Meeting' && !activeFilters.meetings) return false;
    if (item.type === 'Deadline' && !activeFilters.deadlines) return false;
    if (item.type === 'Task' && !activeFilters.tasks) return false;
    return true;
  });

  // Month grid: Monday through Sunday
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  // In JS: Sun = 0, Mon = 1, ..., Sat = 6.
  // For Monday first: Mon = 0, Tue = 1, ..., Sun = 6
  const firstDay = new Date(year, month, 1);
  const firstDayIndexMonFirst = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const daysArray = [];
  for (let i = 0; i < firstDayIndexMonFirst; i++) {
    daysArray.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    daysArray.push(new Date(year, month, d));
  }

  // Selected Day items
  const selectedDateStr = getCellDateStr(selectedDate);
  const selectedDayItems = filteredItems.filter(
    (item) => getItemDateStr(item) === selectedDateStr
  );

  // Active meeting for Meeting details section
  const meetingsForSelectedDay = selectedDayItems.filter((it) => it.type === 'Meeting');
  const activeMeeting =
    selectedMeetingId
      ? items.find((it) => it.id === selectedMeetingId && it.type === 'Meeting')
      : meetingsForSelectedDay[0] ||
        items.find((it) => it.type === 'Meeting' && !isPastTime(it.date)) ||
        items.find((it) => it.type === 'Meeting');

  // Format time helper (e.g. "3:00 PM")
  const formatTime = (dateInput) => {
    try {
      const d = new Date(dateInput);
      return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '';
    }
  };

  // Format meeting range helper (e.g. "3:00-4:00 PM")
  const formatTimeRange = (dateInput, durationHours = 1) => {
    try {
      const start = new Date(dateInput);
      const end = new Date(start.getTime() + durationHours * 3600000);
      const startStr = start.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: start.getMinutes() ? '2-digit' : undefined,
        hour12: true,
      });
      const endStr = end.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: end.getMinutes() ? '2-digit' : undefined,
        hour12: true,
      });
      return `${startStr}-${endStr}`;
    } catch {
      return '3:00-4:00 PM';
    }
  };

  // Formatted selected day heading (e.g. "Thu 30 Oct")
  const formattedDayTitle = selectedDate.toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Top Controls Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">Calendar</h1>
            <span className="text-sm font-semibold text-[#8E8E99]">{monthName}</span>
          </div>
          <div className="flex items-center gap-1 bg-[#131318] p-1 rounded-xl border border-[#2C2C34]">
            <button
              onClick={prevMonth}
              className="p-1 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4] hover:bg-white/[0.04] transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goToToday}
              className="px-2.5 py-1 text-xs font-medium text-[#8E8E99] hover:text-[#F2F2F4] transition-colors"
            >
              Today
            </button>
            <button
              onClick={nextMonth}
              className="p-1 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4] hover:bg-white/[0.04] transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Live Status Tag & Refresh */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={loadCalendarData}
            className="p-2 rounded-xl border border-[#2C2C34] bg-[#131318] text-[#8E8E99] hover:text-white transition-colors"
            title="Refresh Calendar"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#D42B92]/10 border border-[#D42B92]/20 text-[#FF8EC5] text-xs font-semibold">
            <CalendarIcon className="w-3.5 h-3.5" />
            <span>Advisory & Deadlines</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center justify-between gap-4">
          <span>{error}</span>
          <button
            onClick={loadCalendarData}
            className="px-3 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs font-semibold transition-colors shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* Main Grid: Left Column [1] Month View + Right Column [2] Show, [3] Day list, [4] Meeting details */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* ==========================================
            [1] MONTH VIEW
           ========================================== */}
        <div className="lg:col-span-7 bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-[#F2F2F4]">Month view</h2>
            <span className="text-xs text-[#8E8E99] font-medium">
              {currentDate.toLocaleString('en-US', { month: 'short', year: 'numeric' })}
            </span>
          </div>

          {/* Mon -> Sun Week Header */}
          <div className="grid grid-cols-7 text-center text-xs font-medium text-[#8E8E99] pb-2 border-b border-[#2C2C34]/70">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <div key={day} className="py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Day Cells Grid */}
          <div className="grid grid-cols-7 gap-2 text-xs">
            {daysArray.map((cellDate, idx) => {
              if (!cellDate) {
                return <div key={`empty-${idx}`} className="h-16 sm:h-20" />;
              }

              const isToday = isSameDay(cellDate, new Date());
              const isSelected = isSameDay(cellDate, selectedDate);
              const dayStr = getCellDateStr(cellDate);

              // Gather filtered items for this day
              const dayItems = filteredItems.filter((it) => getItemDateStr(it) === dayStr);
              const hasMeeting = dayItems.some((it) => it.type === 'Meeting');
              const hasDeadline = dayItems.some((it) => it.type === 'Deadline');
              const hasTask = dayItems.some((it) => it.type === 'Task');

              return (
                <div
                  key={cellDate.toISOString()}
                  onClick={() => {
                    setSelectedDate(cellDate);
                    // auto-select first meeting on that day if any
                    const meetingOnDay = dayItems.find((it) => it.type === 'Meeting');
                    if (meetingOnDay) {
                      setSelectedMeetingId(meetingOnDay.id);
                    }
                  }}
                  className={`h-16 sm:h-20 p-2 rounded-xl flex flex-col justify-between cursor-pointer transition-all relative ${
                    isSelected
                      ? 'bg-[#321129] border border-[#D42B92] shadow-lg shadow-[#D42B92]/10'
                      : 'bg-[#0E0E12] border border-[#23232A] hover:border-[#383842]'
                  } ${isToday ? 'ring-2 ring-white ring-offset-2 ring-offset-[#131318]' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs font-semibold ${
                        isSelected
                          ? 'text-[#FF8EC5]'
                          : isToday
                          ? 'text-white font-bold'
                          : 'text-[#8E8E99]'
                      }`}
                    >
                      {cellDate.getDate()}
                    </span>
                    {dayItems.length > 0 && (
                      <span className="text-[10px] text-[#6E6E78] font-mono">
                        {dayItems.length}
                      </span>
                    )}
                  </div>

                  {/* Wireframe Dots:
                      Pink = meeting, Orange = deadline, Blue = task */}
                  <div className="flex items-center gap-1.5 mt-auto pt-1">
                    {hasMeeting && (
                      <span
                        className="w-2 h-2 rounded-full bg-[#D42B92] shadow-sm shadow-[#D42B92]/50"
                        title="Meeting"
                      />
                    )}
                    {hasDeadline && (
                      <span
                        className="w-2 h-2 rounded-full bg-[#F59E0B] shadow-sm shadow-[#F59E0B]/50"
                        title="Deadline"
                      />
                    )}
                    {hasTask && (
                      <span
                        className="w-2 h-2 rounded-full bg-[#3B82F6] shadow-sm shadow-[#3B82F6]/50"
                        title="Task"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Color Key matching exact Wireframe specifications */}
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-[#8E8E99] pt-3 border-t border-[#2C2C34]/60">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#D42B92]" />
                <span className="text-[#D42B92] font-medium">Meeting</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                <span className="text-[#F59E0B] font-medium">Deadline</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#3B82F6]" />
                <span className="text-[#3B82F6] font-medium">Task</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-[#8E8E99]">
              <span className="w-3 h-3 rounded-full border border-white inline-block" />
              <span>Today has a white ring</span>
            </div>
          </div>
        </div>

        {/* ==========================================
            RIGHT STACK: [2] Show + [3] Day list + [4] Meeting details
           ========================================== */}
        <div className="lg:col-span-5 space-y-4">
          {/* ==========================================
              [2] SHOW (FILTERS · REUSE)
             ========================================== */}
          <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-[#F2F2F4]">Show</h3>
              <span className="text-[11px] text-[#8E8E99]">Filters · Toggle on / off</span>
            </div>
            <div className="flex items-center gap-2.5 flex-wrap">
              {/* Meetings Pill */}
              <button
                type="button"
                onClick={() =>
                  setActiveFilters((prev) => ({ ...prev, meetings: !prev.meetings }))
                }
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  activeFilters.meetings
                    ? 'bg-[#D42B92] text-white shadow-md shadow-[#D42B92]/30 ring-1 ring-[#D42B92]'
                    : 'bg-[#18181F] text-[#8E8E99] border border-[#2C2C34] hover:text-white'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    activeFilters.meetings ? 'bg-white' : 'bg-[#D42B92]'
                  }`}
                />
                <span>Meetings</span>
              </button>

              {/* Deadlines Pill */}
              <button
                type="button"
                onClick={() =>
                  setActiveFilters((prev) => ({ ...prev, deadlines: !prev.deadlines }))
                }
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  activeFilters.deadlines
                    ? 'bg-[#F59E0B] text-black font-bold shadow-md shadow-[#F59E0B]/30 ring-1 ring-[#F59E0B]'
                    : 'bg-[#18181F] text-[#8E8E99] border border-[#2C2C34] hover:text-white'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    activeFilters.deadlines ? 'bg-black' : 'bg-[#F59E0B]'
                  }`}
                />
                <span>Deadlines</span>
              </button>

              {/* Tasks Pill */}
              <button
                type="button"
                onClick={() =>
                  setActiveFilters((prev) => ({ ...prev, tasks: !prev.tasks }))
                }
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  activeFilters.tasks
                    ? 'bg-[#3B82F6] text-white shadow-md shadow-[#3B82F6]/30 ring-1 ring-[#3B82F6]'
                    : 'bg-[#18181F] text-[#8E8E99] border border-[#2C2C34] hover:text-white'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    activeFilters.tasks ? 'bg-white' : 'bg-[#3B82F6]'
                  }`}
                />
                <span>Tasks</span>
              </button>
            </div>
          </div>

          {/* ==========================================
              [3] DAY LIST · MODIFY
             ========================================== */}
          <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
              <h3 className="text-sm font-bold text-[#F2F2F4]">{formattedDayTitle}</h3>
              <span className="text-[11px] text-[#8E8E99]">
                {selectedDayItems.length} {selectedDayItems.length === 1 ? 'item' : 'items'}
              </span>
            </div>

            {selectedDayItems.length === 0 ? (
              <div className="py-8 text-center text-xs text-[#8E8E99]">
                No items scheduled for this date. Click on any date with a dot marker to view items.
              </div>
            ) : (
              <div className="space-y-2.5">
                {selectedDayItems.map((item) => {
                  const isMeeting = item.type === 'Meeting';
                  const isDeadline = item.type === 'Deadline';
                  const isTask = item.type === 'Task';
                  const isPast = isPastTime(item.date);
                  const isSelectedForDetail = activeMeeting?.id === item.id;

                  // Subtitle formatting matching wireframe
                  let subtitle = '';
                  if (isMeeting) {
                    subtitle = `${formatTime(item.date)} · Meeting`;
                  } else if (isDeadline) {
                    subtitle = `${formatTime(item.date) || '11:59 PM'} · Deadline`;
                  } else {
                    subtitle = `Task · ${item.priority || 'High'}`;
                  }

                  return (
                    <div
                      key={item.id}
                      onClick={() => {
                        if (isMeeting) setSelectedMeetingId(item.id);
                      }}
                      className={`p-3 rounded-xl bg-[#08080A] border transition-all flex items-center justify-between gap-3 cursor-pointer ${
                        isSelectedForDetail
                          ? 'border-[#D42B92]/60 bg-[#1A0E18]'
                          : 'border-[#2C2C34] hover:border-[#3E3E48]'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-[#F2F2F4] truncate">
                          {item.title}
                        </div>
                        <div className="text-[11px] text-[#8E8E99] mt-0.5">{subtitle}</div>
                      </div>

                      {/* Right action / badge based on type */}
                      <div className="shrink-0 flex items-center gap-1.5">
                        {isMeeting && (
                          <>
                            {item.meetingLink && !isPast && item.status !== 'Cancelled' ? (
                              <a
                                href={item.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="px-3.5 py-1 rounded-full border border-[#D42B92] text-[#FF8EC5] hover:bg-[#D42B92] hover:text-white text-xs font-semibold transition-all inline-flex items-center gap-1"
                              >
                                <span>Join</span>
                                <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            ) : isPast ? (
                              <span className="text-[10px] text-[#8E8E99] font-medium px-2 py-0.5 rounded bg-white/[0.04]">
                                Completed
                              </span>
                            ) : null}
                          </>
                        )}

                        {isDeadline && (
                          <span className="px-3 py-0.5 rounded-full border border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B] text-xs font-semibold">
                            Due
                          </span>
                        )}

                        {isTask && (
                          <span className="px-3 py-0.5 rounded-full border border-[#3B82F6]/50 bg-[#3B82F6]/10 text-[#60A5FA] text-xs font-semibold">
                            {item.status === 'Completed' ? 'Done' : 'In Prog.'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ==========================================
              [4] MEETING DETAILS · BUILD NEW
             ========================================== */}
          <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
              <h3 className="text-sm font-bold text-[#F2F2F4]">Meeting details</h3>
              {activeMeeting && (
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-[#D42B92]/20 text-[#FF8EC5]">
                  {activeMeeting.status || 'Scheduled'}
                </span>
              )}
            </div>

            {activeMeeting ? (
              <div className="space-y-3">
                {/* Organiser + Time */}
                <div>
                  <div className="text-xs font-semibold text-[#F2F2F4]">
                    With {activeMeeting.mentorName || 'Mentor Riya'} ·{' '}
                    {formatTimeRange(activeMeeting.date, activeMeeting.durationHours || 1)}
                  </div>
                  <div className="text-xs text-[#8E8E99] mt-1">
                    Agenda: {activeMeeting.agenda || activeMeeting.description || 'deck v3 walkthrough'}
                  </div>
                </div>

                {/* Subtext */}
                <div className="text-[11px] text-[#6E6E78]">Link opens in a new tab</div>

                {/* Join meeting button */}
                <div>
                  {activeMeeting.meetingLink &&
                  !isPastTime(activeMeeting.date) &&
                  activeMeeting.status !== 'Cancelled' ? (
                    <a
                      href={activeMeeting.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-2.5 px-4 rounded-full bg-gradient-to-r from-[#D42B92] to-[#b02279] hover:brightness-110 text-white font-semibold text-xs transition-all shadow-md shadow-[#D42B92]/30 flex items-center justify-center gap-1.5"
                    >
                      <span>Join meeting</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : isPastTime(activeMeeting.date) ? (
                    <div className="w-full py-2 px-3 rounded-xl bg-white/[0.04] border border-[#2C2C34] text-center text-xs text-[#8E8E99]">
                      Past meeting — Join link expired
                    </div>
                  ) : (
                    <div className="w-full py-2 px-3 rounded-xl bg-white/[0.04] border border-[#2C2C34] text-center text-xs text-[#8E8E99]">
                      No link provided for this meeting
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="py-6 text-center text-xs text-[#8E8E99]">
                Select a meeting from the day list to view organiser, agenda, and join link.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
